import {
  Box,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  IconButton,
  ListItem,
  Stack,
  SvgIcon,
  Typography,
} from '@mui/material';

import { ReactComponent as Github } from 'assets/images/ic_github.svg';
import { ReactComponent as Plus } from 'assets/images/ic_plus.svg';
import { Formik, FormikHelpers } from 'formik';
import { useEffect, useState } from 'react';
import { DiscoverAIService } from 'services/DiscoverAIService';
import {
  AppFormikPasswordField,
  AppFormikTextField,
  AppPrimaryButton,
  AppPrimaryButtonOutlined,
  ConfirmDialog,
} from 'shared/components/AppComponents';
import { Toast, Utils } from 'shared/helpers';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { BaseApplicationState } from 'state/baseapplication/Reducer';
import { useSelector } from 'react-redux';

export interface ICredentials {
  _id: string;
  username: string;
  accessToken: string;
  connectiontype: string;
  isActive: boolean;
  createdBy: string;
  organizationId: string;
  createdAt: string;
  updatedAt: string;
  __v: number;
}

const ActiveGithubCard = (): React.ReactElement => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [credentials, setCredentials] = useState<ICredentials[]>([]);
  const [deleteRecord, setDeleteRecord] = useState<ICredentials>();
  const [isLoading, setIsLoading] = useState(false);
  const [repoFor, setRepoFor] = useState<ICredentials>();
  const [repos, setRepos] = useState<{ name: string }[]>([]);
  const { orgACLConfig }: BaseApplicationState = useSelector(
    (state: any) => state.baseAppState
  );
  // API Calls
  const getConnections = (): void => {
    setIsLoading(true);
    DiscoverAIService.getConnections('github')
      .then((response: any) => {
        const { data } = response.data;
        setCredentials(data);
      })
      .catch((error: any) => {
        Utils.handleErrorResponse(error);
      })
      .finally(() => setIsLoading(false));
  };
  const deleteConnection = (id: string, callback: any): void => {
    DiscoverAIService.deleteConnection(id)
      .then((response: any) => {
        callback(true, response);
      })
      .catch((error: any) => {
        Utils.handleErrorResponse(error);
        callback(false, error);
      });
  };
  const getRepos = (id: string): void => {
    setIsLoading(true);
    DiscoverAIService.getGitRepos(id)
      .then((response: any) => {
        const { data } = response.data;
        setRepos(data);
      })
      .catch((error: any) => {
        Utils.handleErrorResponse(error);
      })
      .finally(() => setIsLoading(false));
  };
  const createConnection = (formValues: any, callback: any): void => {
    DiscoverAIService.createConnection({ ...formValues })
      .then((response: any) => {
        callback(true, response);
      })
      .catch((error: any) => {
        callback(false);
        Utils.handleErrorResponse(error);
      });
  };

  const handleCloseDelete = (): void => {
    setDeleteRecord(undefined);
  };

  const handleDeleteRecord = (): void => {
    deleteConnection(`${deleteRecord?._id}`, (status: boolean) => {
      handleCloseDelete();
      if (status) {
        getConnections();
        Toast.showSuccessMessage('Connection successfully deleted');
      }
    });
  };

  const actionOnFormSubmit = (
    formValues: any,
    formik: FormikHelpers<any>
  ): void => {
    if (formik) {
      createConnection({ ...formValues }, (status: boolean) => {
        formik.setSubmitting(false);
        setShowAddForm(false);

        if (status) {
          getConnections();
          Toast.showSuccessMessage('Connection successfully added');
        }
      });
    }
  };

  // Hooks
  useEffect(() => {
    getConnections();
  }, []);

  // UI Elements
  return (
    <Card variant="outlined">
      <CardHeader
        avatar={
          <SvgIcon
            component={Github}
            inheritViewBox
            className="view-icon"
            fontSize="inherit"
            htmlColor="#76A81F"
          />
        }
        title="GitHub Integration"
        subheader="Connect to GitHub repositories to scan for AI systems and models"
      />

      {isLoading && (
        <Box className="empty-content">
          <CircularProgress />
        </Box>
      )}
      {!isLoading && (
        <>
          {showAddForm && (
            <CardContent>
              <Box className="app-box-border px-5">
                <Typography className="f18 fw-5">
                  New GitHub Connection
                </Typography>
                <Typography className="text-gray mb-5">
                  Add a new GitHub connection using a personal access token
                </Typography>

                <Formik
                  enableReinitialize
                  initialValues={{
                    username: '',
                    accessToken: '',
                    connectiontype: 'github',
                  }}
                  validateOnMount
                  onSubmit={(formValues, formik) => {
                    actionOnFormSubmit(formValues, formik);
                  }}
                >
                  {({ handleSubmit, isSubmitting }) => (
                    <form autoComplete="off">
                      <AppFormikTextField
                        outerLabel
                        label="Connection Name"
                        name="username"
                        placeholder="Enter username"
                      />

                      <AppFormikPasswordField
                        outerLabel
                        label="GitHub Personal Access Token"
                        name="accessToken"
                        placeholder="Enter access token"
                      />
                      <Typography variant="body1" className="text-gray">
                        Token should have 'repo' scope to read repositories
                      </Typography>

                      <Stack
                        justifyContent="flex-end"
                        direction="row"
                        spacing={5}
                      >
                        <AppPrimaryButtonOutlined
                          className="btn-small"
                          title="Cancel"
                          onClick={() => {
                            setShowAddForm(false);
                          }}
                        />
                        <AppPrimaryButton
                          className="btn-small"
                          title="Create Connection"
                          isLoading={isSubmitting}
                          loadingSize={22}
                          loadingText="CREATING"
                          disabled={isSubmitting}
                          onClick={handleSubmit}
                        />
                      </Stack>
                    </form>
                  )}
                </Formik>
              </Box>
            </CardContent>
          )}

          {credentials.length > 0 && !showAddForm && (
            <Stack direction="row" justifyContent="flex-end">
              {orgACLConfig.aiInventory?.includes(
                'aiInventory.discoveryAI.gitHub.addConnection'
              ) && (
                <AppPrimaryButton
                  className="btn-small mr-5"
                  title="Add GitHub Connection"
                  startIcon={
                    <SvgIcon
                      component={Plus}
                      inheritViewBox
                      className="view-icon"
                      fontSize="inherit"
                      htmlColor="#76A81F"
                    />
                  }
                  onClick={() => setShowAddForm(true)}
                />
              )}
            </Stack>
          )}
          <CardContent>
            {credentials.map((credential) => (
              <ListItem
                key={credential._id}
                className="app-box-border mt-4 static-box"
                onClick={() => {
                  getRepos(credential._id);
                  setRepoFor(credential);
                }}
              >
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ width: '100%' }}
                >
                  <Box>
                    <Stack
                      className="text-primary mb-2"
                      alignItems="center"
                      direction="row"
                      spacing={2}
                    >
                      <SvgIcon
                        component={Github}
                        inheritViewBox
                        className="view-icon"
                        fontSize="inherit"
                        htmlColor="#76A81F"
                      />
                      <Typography className="f18 fw-6">
                        {credential.username}
                      </Typography>
                    </Stack>
                    <Typography variant="body1" className="text-gray mb-2">
                      Created {Utils.formatDateTime(credential.createdAt)}
                    </Typography>
                  </Box>
                  {orgACLConfig.aiInventory?.includes(
                    'aiInventory.discoveryAI.gitHub.deleteConnection'
                  ) && (
                    <IconButton
                      onClick={() => {
                        setDeleteRecord(credential);
                      }}
                      title="Delete"
                    >
                      <DeleteOutlineIcon />
                    </IconButton>
                  )}
                </Stack>
              </ListItem>
            ))}

            {repoFor && (
              <>
                <Typography className="f18 my-5">
                  Repos for <b>{repoFor?.username}</b>
                </Typography>
                {repos.map((repo) => (
                  <ListItem
                    key={repo.name}
                    className="app-box-border mt-4 static-box"
                  >
                    <Stack direction="row" spacing={2} alignItems="center">
                      <SvgIcon
                        component={Github}
                        inheritViewBox
                        className="view-icon"
                        fontSize="inherit"
                        htmlColor="#76A81F"
                      />
                      <Typography className="f16 fw-5">{repo.name}</Typography>
                    </Stack>
                  </ListItem>
                ))}
                {repos.length === 0 && (
                  <Box className="empty-content">
                    <Typography className="f16">No repos found</Typography>
                  </Box>
                )}
              </>
            )}
          </CardContent>

          {!showAddForm && credentials.length === 0 && (
            <CardContent>
              <Stack
                direction="column"
                spacing={4}
                justifyContent="center"
                alignItems="center"
              >
                <Stack direction="row" spacing={2} alignItems="center">
                  <SvgIcon
                    component={Github}
                    inheritViewBox
                    className="view-icon"
                    fontSize="inherit"
                    htmlColor="#76A81F"
                  />
                  <Typography variant="h6">No GitHub Connections</Typography>
                </Stack>
                <Typography component="span">
                  Connect to GitHub to scan repositories for AI systems
                </Typography>
                <AppPrimaryButton
                  title="Add GitHub Connection"
                  startIcon={
                    <SvgIcon
                      component={Plus}
                      inheritViewBox
                      className="view-icon"
                      fontSize="inherit"
                      htmlColor="#76A81F"
                    />
                  }
                  onClick={() => setShowAddForm(true)}
                />
              </Stack>
            </CardContent>
          )}
        </>
      )}

      <ConfirmDialog
        title="Delete Connection"
        subTitle="Are you sure to delete the connection?"
        showConfirm={deleteRecord}
        cancelTitle="Cancel"
        successTitle="Delete"
        onSuccess={() => {
          handleDeleteRecord();
        }}
        onCancel={handleCloseDelete}
        onCloseDialog={handleCloseDelete}
      />
    </Card>
  );
};

export default ActiveGithubCard;
